/**
 * StandPlanBolumu.jsx — the "Stand Planı" section of the project stand detail (backlog #209).
 *
 * Two read-only fdsVector views of THIS stand next to each other, replacing the old "Kroki" tab (which embedded
 * the whole hall and, for a hall with no plan, a demo hall of another fair):
 *   "Standın planı"      only this stand's shape (standIds + the Stand margin)         - the stand's own plan
 *   "Salondaki konumu"   the whole hall, no layer panel, only this stand marked/framed - its place in the hall
 * (the same view options as the Stand Detay "Konum" tab, StandKonumKarti, margins from Sistem Ayarları › Plan Ayarları).
 * "Planı Görüntüle" opens StandKonumKarti in a large window: the three views (Alan / Salon / Yalnızca Stand) at full size.
 *
 * There is NO demo-plan fallback. What the section says instead (planDurumu):
 *   salonYok    the stand has no hall
 *   planYok     the hall's plan is not drawn for this fair yet (fdsVector has no document for the pair)
 *   cizilmemis  the plan exists but this stand is not linked to a shape in it - asked from FDS-Backend's own
 *               link table (GET /api/v2/vector/stands/{id}/shapes, backlog #167), not guessed from the iframe
 * If the link table cannot be read (older backend, no staff session) the plan is shown anyway and the embedded
 * plan's own "isolate" report decides whether to warn.
 *
 * `s`: the stand { standId, standName, salonId, salonName, fuarId }.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { AlertTriangle, Map as MapIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import KrokiVectorEmbed from "./KrokiVectorEmbed";
import StandKonumKarti from "./StandKonumKarti";
import { resolveStandPlanDocument } from "../../services/krokiVectorService";
import { getKonumMargins } from "../../services/systemSettingsService";
import { getStandShapes, standDrawnInHall } from "../../services/standPlanService";

/**
 * What the section shows. `probe` = resolveStandPlanDocument's answer (null until it came), `shapes` = getStandShapes'
 * answer (null until it came), `margins` = getKonumMargins' (null until it came).
 * @returns {"salonYok"|"yukleniyor"|"planYok"|"cizilmemis"|"goster"}
 */
export function planDurumu({ salonId, probe, shapes, margins }) {
  if (salonId == null || salonId === "") return "salonYok";
  if (!probe) return "yukleniyor";
  if (probe.reason === "not-mapped" || probe.reason === "missing-id") return "planYok";
  if (!shapes || !margins) return "yukleniyor";
  if (shapes.ok && !standDrawnInHall(shapes.shapes, salonId)) return "cizilmemis";
  return "goster";
}

/** The view options of the two previews (the same as StandKonumKarti's Yalnızca Stand and Salon views). */
export function onizlemeSecenekleri(standId, margins) {
  return {
    stand: { standIds: [standId], margins: margins?.stand },
    salon: { layers: "none", selectedStandId: standId, fit: "hall", margins: margins?.salon },
  };
}

export default function StandPlanBolumu({ s }) {
  const { t } = useTranslation("projeler");
  const [margins, setMargins] = useState(null);
  const [modalAcik, setModalAcik] = useState(false);
  // Answers are keyed by what they were asked for, so a stale answer of another stand/hall never shows.
  const probeKey = `${s.fuarId}|${s.salonId}`;
  const [probeSonuc, setProbeSonuc] = useState(null);
  const [shapesSonuc, setShapesSonuc] = useState(null);
  const [isolateSonuc, setIsolateSonuc] = useState(null);
  const probe = probeSonuc?.key === probeKey ? probeSonuc.sonuc : null;
  const shapes = shapesSonuc?.standId === s.standId ? shapesSonuc.sonuc : null;

  useEffect(() => {
    let ignore = false;
    getKonumMargins().then((m) => { if (!ignore) setMargins(m); });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!s.salonId) return undefined;
    let ignore = false;
    resolveStandPlanDocument({ fairId: s.fuarId, hallId: s.salonId })
      .then((sonuc) => { if (!ignore) setProbeSonuc({ key: probeKey, sonuc }); });
    return () => { ignore = true; };
  }, [probeKey, s.fuarId, s.salonId]);

  useEffect(() => {
    let ignore = false;
    getStandShapes(s.standId).then((sonuc) => { if (!ignore) setShapesSonuc({ standId: s.standId, sonuc }); });
    return () => { ignore = true; };
  }, [s.standId]);

  const durum = planDurumu({ salonId: s.salonId, probe, shapes, margins });
  const secenekler = onizlemeSecenekleri(s.standId, margins);
  // The link table could not be read: the embedded plan reports whether the stand is on it.
  const bagliDegil = durum === "goster" && shapes && !shapes.ok && isolateSonuc != null && (
    isolateSonuc.matched === 0 || (isolateSonuc.missingStandIds ?? []).map(String).includes(String(s.standId))
  );

  let govde;
  if (durum === "salonYok") {
    govde = <div className="text-muted">{t("standPlan.salonYok")}</div>;
  } else if (durum === "yukleniyor") {
    govde = (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <div className="text-muted small mt-2">{t("standPlan.araniyor")}</div>
      </div>
    );
  } else if (durum === "planYok") {
    govde = (
      <Alert color="warning" className="d-flex align-items-start gap-2 mb-0" data-testid="plan-yok">
        <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
        <div>{t("standPlan.planYok", { salon: s.salonName || t("standPlan.buSalon") })}</div>
      </Alert>
    );
  } else if (durum === "cizilmemis") {
    govde = (
      <Alert color="warning" className="d-flex align-items-start gap-2 mb-0" data-testid="stand-cizilmemis">
        <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
        <div>{t("standPlan.standCizilmemis", { stand: s.standName })}</div>
      </Alert>
    );
  } else {
    govde = (
      <>
        {probe && !probe.ok && (
          <Alert color="warning" className="py-2 small">{t("standPlan.dogrulanamadi", { message: probe.message })}</Alert>
        )}
        <Row className="g-3">
          <Col md={6}>
            <div className="fw-semibold small mb-1">{t("standPlan.standinPlani")}</div>
            <KrokiVectorEmbed
              fairId={s.fuarId}
              hallId={s.salonId}
              hallName={s.salonName}
              height={300}
              allowEdit={false}
              gelistiriciAraclari={false}
              viewOptions={secenekler.stand}
              onIsolateResult={(r) => setIsolateSonuc(r)}
            />
          </Col>
          <Col md={6}>
            <div className="fw-semibold small mb-1">{t("standPlan.salondakiKonumu")}</div>
            <KrokiVectorEmbed
              fairId={s.fuarId}
              hallId={s.salonId}
              hallName={s.salonName}
              height={300}
              allowEdit={false}
              gelistiriciAraclari={false}
              viewOptions={secenekler.salon}
            />
          </Col>
        </Row>
        {bagliDegil && (
          <Alert color="warning" className="py-2 small mt-2 mb-0" data-testid="stand-bagli-degil">
            {t("standPlan.standCizilmemis", { stand: s.standName })}
          </Alert>
        )}
      </>
    );
  }

  return (
    <>
      <Card className="mt-3" data-testid="stand-plan-bolumu">
        <CardHeader className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span className="fw-semibold">
            {t("standPlan.baslik")}
            {s.salonName ? <span className="text-muted fw-normal">{" · "}{s.salonName}</span> : null}
          </span>
          <Button size="sm" color="primary" outline disabled={!s.salonId} onClick={() => setModalAcik(true)} data-testid="plani-goruntule">
            <MapIcon size={14} className="me-1" />{t("standPlan.planiGoruntule")}
          </Button>
        </CardHeader>
        <CardBody>{govde}</CardBody>
      </Card>

      <Modal isOpen={modalAcik} toggle={() => setModalAcik(false)} size="xl">
        <ModalHeader toggle={() => setModalAcik(false)}>
          {t("standPlan.planiGoruntuleBaslik", { stand: s.standName })}
        </ModalHeader>
        <ModalBody>
          <StandKonumKarti s={s} mod="personel" />
        </ModalBody>
      </Modal>
    </>
  );
}
